'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef, useState } from 'react';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';

import Link from 'next/link';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { Swiper as SwiperApi } from 'swiper/types';

import { MainSlideDataType } from '@screens/Main/model/types/mainPageData';
import { Container } from '@shared/ui/Container';
import { ResponsiveImage } from '@shared/ui/ResponsiveImage';

import cls from './MainSlider.module.scss';

type MainSliderProps = {
  data: MainSlideDataType[];
};

export const MainSlider = ({ data }: MainSliderProps) => {
  const filteredSlides = data.filter((el) => el.active);

  const [swiper, setSwiper] = useState<SwiperApi>();

  const progressCircle = useRef<SVGSVGElement>(null);
  const progressContent = useRef<HTMLSpanElement>(null);

  const onAutoplayTimeLeft: SwiperProps['onAutoplayTimeLeft'] = (_, __, progress) => {
    progressCircle?.current?.style?.setProperty('--progress', String(1 - progress));
  };

  const onInit: SwiperProps['onInit'] = (swiper) => {
    setSwiper(swiper);
  };

  const onClickPrev = () => {
    if (swiper?.realIndex === 0) {
      swiper?.slideTo(filteredSlides.length - 1);
    } else {
      swiper?.slidePrev();
    }
  };

  const onClickNext = () => {
    if (swiper?.realIndex === filteredSlides.length - 1) {
      swiper?.slideTo(0);
    } else {
      swiper?.slideNext();
    }
  };

  if (!filteredSlides.length) return;

  return (
    <Container className={cls.mainSliderWrapper}>
      <div className={cls.content}>
        <Swiper
          className={cls.mainSlider}
          modules={[Pagination, Autoplay, EffectFade]}
          pagination={{ clickable: true }}
          speed={1000}
          slidesPerView={1}
          effect={'fade'}
          autoplay={{
            delay: 3000
          }}
          {...{ onInit, onAutoplayTimeLeft }}
        >
          {filteredSlides.map((item, index) => (
            <SwiperSlide
              className={cls.slide}
              key={item.imageDesktop}
            >
              {item?.link ? (
                <Link
                  href={item.link}
                  target={'_blank'}
                >
                  <ResponsiveImage
                    desktopSrc={item.imageDesktop}
                    mobileSrc={item.imageAdaptive}
                    fill
                    priority
                    alt={index ? 'Современная мебель для ванной и туалета' : 'Комплект мебели для ванной комнаты'}
                  />
                </Link>
              ) : (
                <ResponsiveImage
                  desktopSrc={item.imageDesktop}
                  mobileSrc={item.imageAdaptive}
                  fill
                  priority
                  alt={index ? 'Современная мебель для ванной и туалета' : 'Комплект мебели для ванной комнаты'}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
        {filteredSlides.length > 1 && (
          <button
            aria-label={'предыдущий слайд'}
            className={cls.mainSliderButtonPrev}
            onClick={onClickPrev}
          >
            <ChevronLeft />
          </button>
        )}

        {filteredSlides.length > 1 && (
          <button
            aria-label={'следующий слайд'}
            className={cls.mainSliderButtonNext}
            onClick={onClickNext}
          >
            <ChevronRight />
            <div className={cls.circleProgressWrapper}>
              <svg
                className={cls.circleProgress}
                viewBox="0 0 48 48"
                ref={progressCircle}
              >
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                ></circle>
              </svg>
              <span ref={progressContent}></span>
            </div>
          </button>
        )}
      </div>
    </Container>
  );
};
